import { createContext, useContext, useMemo, ReactNode } from "react";
import {
  detectPortal,
  getPortalUrl,
  getPortalForRole,
  PORTAL_LABELS,
  PORTAL_DESCRIPTIONS,
  PORTAL_ROLES,
  type PortalId,
} from "@/lib/portal";

type PortalContextType = {
  /** Which portal subdomain we are on. null = main domain, no portal context. */
  portal: PortalId | null;
  /** Human-readable portal name, e.g. "Merchant Portal" */
  label: string;
  /** Login page subtitle for this portal */
  description: string;
  /** Roles that are allowed to authenticate on this portal */
  allowedRoles: string[];
  /** True when running on a portal subdomain (or VITE_PORTAL is set) */
  isPortalMode: boolean;
  /**
   * Build a URL for a specific portal.
   * In production this returns a full cross-subdomain URL.
   * In dev without VITE_PORTAL it returns a plain path.
   */
  getPortalUrl: (portalId: PortalId, path?: string) => string;
  /** Given a user role, return the portal it belongs to (null if none). */
  getPortalForRole: (role: string) => PortalId | null;
};

const PortalContext = createContext<PortalContextType | undefined>(undefined);

export const PortalProvider = ({ children }: { children: ReactNode }) => {
  const portal = useMemo(() => detectPortal(), []);

  const value = useMemo<PortalContextType>(
    () => ({
      portal,
      label: portal ? PORTAL_LABELS[portal] : "VGV Platform",
      description: portal
        ? PORTAL_DESCRIPTIONS[portal]
        : "Access your portal to manage vouchers and grow your business.",
      allowedRoles: portal ? PORTAL_ROLES[portal] : [],
      isPortalMode: portal !== null,
      getPortalUrl,
      getPortalForRole,
    }),
    [portal]
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePortal = (): PortalContextType => {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error("usePortal must be used within PortalProvider");
  return ctx;
};
